import { Stack } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';

import { TFetchError } from '@/api/api-error.types';
import { _getContactCodeUser, _getQrTokenUser } from '@/api/user/mutations';
import { useGetUserProfile } from '@/api/user-dashboard/queries';
import QrCodeIcon from '@/assets/svg/ui-elements/qr-code.svg';
import { OtpInput, Spinner, theme, Typography } from '@/shared/design-system';

import { StepFooter } from '../components/step-footer';
import type { ContactData } from '../types';

const OTP_LENGTH = 6;

// The typed fallback is always exactly 6 digits (OtpInput strips anything else). A scanned
// personal QR resolves to the longer `<ref>.<ent>.<exp>.<sig>` token instead, so anything that
// isn't a 6-digit code is routed to the token lookup.
const isSixDigitCode = (value: string) => /^\d{6}$/.test(value);

interface QrScanStepProps {
  initialCode?: string;
  onCodeConsumed?: () => void;
  onScanned: (data: Partial<ContactData>, referenceId: string, contactCode: string) => void;
  onBack: () => void;
}

export const QrScanStep = ({ initialCode, onCodeConsumed, onScanned, onBack }: QrScanStepProps) => {
  // A token never belongs in the six-box `OtpInput` — it's ~87 characters and the widget is
  // fixed at 6. Only a 6-digit `initialCode` pre-fills `code`; a token is resolved directly from
  // `initialCode` in the auto-submit effect below without ever touching this state.
  const [code, setCode] = useState(initialCode && isSixDigitCode(initialCode) ? initialCode : '');
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState('');
  const hasAutoCalledRef = useRef(false);
  const { user: sessionUser } = useGetUserProfile();

  const onChange = useCallback((val: string) => {
    setCode(val);
    setError('');
  }, []);

  const resolve = useCallback(
    async (value: string) => {
      setIsPending(true);
      setError('');
      try {
        const user = isSixDigitCode(value)
          ? await _getContactCodeUser(value)
          : await _getQrTokenUser(value);
        if (sessionUser?.id && user.id === sessionUser.id) {
          setError("This is your own QR code — you can't add yourself as a contact");
          if (!isSixDigitCode(value)) setCode('');
          return;
        }
        onScanned({ name: user.name ?? undefined }, user.id, value);
      } catch (e) {
        const err = e as TFetchError;
        setError(err.data?.data?.details || err.data?.message || "We can't find this user");
        // A failed token has nowhere useful to sit in a 6-digit box — leave the field empty and
        // editable so a token-shaped failure still ends in the same usable state as a typed
        // 6-digit failure: an error message plus a field the person can type into.
        if (!isSixDigitCode(value)) setCode('');
      } finally {
        setIsPending(false);
      }
    },
    [onScanned, sessionUser?.id],
  );

  const handleConnect = useCallback(() => resolve(code), [resolve, code]);

  useEffect(() => {
    // A pre-filled value auto-submits regardless of whether it's a 6-digit code or a scanned
    // token — resolve routes to the right resolver either way. Resolving `initialCode` directly
    // (not via `code` state) is what keeps a token out of the OtpInput in the first place.
    if (!initialCode || hasAutoCalledRef.current) return;
    hasAutoCalledRef.current = true;
    onCodeConsumed?.();
    resolve(initialCode);
  }, [resolve, initialCode, onCodeConsumed]);

  return (
    <Stack flexDirection="column" alignItems="center" gap="24px">
      <QrCodeIcon width={24} height={24} color={theme.colors.icon.brand} />

      <Stack
        sx={{
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          backgroundColor: theme.colors.black[30],
          p: '20px',
          borderRadius: '16px',
        }}
      >
        {isPending ? (
          <Spinner />
        ) : (
          <OtpInput
            length={OTP_LENGTH}
            placeholder="1"
            error={!!error}
            value={code}
            onChange={onChange}
          />
        )}

        {error && (
          <Typography variant="body4" color={theme.colors.text.error}>
            {error}
          </Typography>
        )}
      </Stack>

      <StepFooter
        onBack={onBack}
        onNext={handleConnect}
        nextLabel="Connect"
        nextDisabled={code.length !== OTP_LENGTH || isPending}
      />
    </Stack>
  );
};
